import React, { useState } from 'react';
import { ArrowUp, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const closeModal = () => setActiveModal(null);
  return <>
      <footer className="bg-[#F8FAFC] py-12 border-t border-[#E2E8F0]" data-id="element-113">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-114">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6" data-id="element-115">
            <div className="text-center md:text-left" data-id="element-116">
              <h4 className="font-bold text-[#1E293B] mb-2" data-id="element-117">
                French Bulldog Smart Feeding System
              </h4>
              <p className="text-sm text-[#475569] max-w-md" data-id="element-118">
                This guide is for educational purposes only and does not replace
                professional veterinary advice.
              </p>
            </div>

            <button onClick={scrollToTop} className="flex items-center text-sm font-medium text-[#0F766E] hover:text-[#0D9488] transition-colors" data-id="element-119">
              Back to top
              <ArrowUp className="w-4 h-4 ml-2" data-id="element-120" />
            </button>
          </div>

          <div className="mt-8 pt-8 border-t border-[#E2E8F0] flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8" data-id="element-121">
            <button onClick={() => setActiveModal('privacy')} className="text-sm text-[#475569] hover:text-[#0F766E] transition-colors" data-id="element-122">
              Privacy Policy
            </button>
            <button onClick={() => setActiveModal('terms')} className="text-sm text-[#475569] hover:text-[#0F766E] transition-colors" data-id="element-123">
              Terms and Conditions
            </button>
          </div>

          <div className="mt-6 text-center text-xs text-[#94A3B8]" data-id="element-124">
            © {new Date().getFullYear()} French Bulldog Smart Feeding System.
            All rights reserved.
          </div>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-125">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" onClick={closeModal} data-id="element-126">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.2
        }} className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-127">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-[#E2E8F0]" data-id="element-128">
                <h2 className="text-xl font-bold text-[#1E293B]" data-id="element-129">
                  {activeModal === 'privacy' && 'Privacy Policy'}
                  {activeModal === 'terms' && 'Terms and Conditions'}
                </h2>
                <button onClick={closeModal} className="p-2 rounded-full hover:bg-[#F8FAFC] transition-colors text-[#475569]" data-id="element-130">
                  <X className="w-5 h-5" data-id="element-131" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto text-sm text-[#475569] space-y-4" data-id="element-132">
                {activeModal === 'privacy' ? <>
                    <p className="font-semibold text-[#1E293B]" data-id="element-133">
                      Last updated: January 2026
                    </p>
                    <h3 className="font-bold text-[#1E293B] mt-4" data-id="element-134">
                      1. Information We Collect
                    </h3>
                    <p data-id="element-135">
                      We collect information you provide directly to us, such as
                      your name, email address, and payment details when you
                      make a purchase. We may also collect information about
                      your device and how you interact with our site.
                    </p>
                    <h3 className="font-bold text-[#1E293B] mt-4" data-id="element-136">
                      2. Use of Information
                    </h3>
                    <p data-id="element-137">
                      We use the collected information to process your orders,
                      send updates, improve our products, and communicate with
                      you.
                    </p>
                    <h3 className="font-bold text-[#1E293B] mt-4" data-id="element-138">
                      3. Data Protection
                    </h3>
                    <p data-id="element-139">
                      We implement technical and organizational security
                      measures to protect your personal information against
                      unauthorized access, loss, or alteration.
                    </p>
                  </> : <>
                    <p className="font-semibold text-[#1E293B]" data-id="element-140">
                      Last updated: January 2026
                    </p>
                    <h3 className="font-bold text-[#1E293B] mt-4" data-id="element-141">
                      1. Acceptance of Terms
                    </h3>
                    <p data-id="element-142">
                      By accessing and using this website and purchasing our
                      products, you accept these terms and conditions in their
                      entirety.
                    </p>
                    <h3 className="font-bold text-[#1E293B] mt-4" data-id="element-143">
                      2. Products and Services
                    </h3>
                    <p data-id="element-144">
                      We offer educational digital content about canine
                      nutrition. Our products are informational guides and do
                      not substitute professional veterinary advice.
                    </p>
                    <h3 className="font-bold text-[#1E293B] mt-4" data-id="element-145">
                      3. Refund Policy
                    </h3>
                    <p data-id="element-146">
                      We offer a satisfaction guarantee. If you are not
                      satisfied with your purchase, you can request a refund
                      within 7 days of purchase.
                    </p>
                  </>}
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-[#E2E8F0] bg-[#F8FAFC]" data-id="element-147">
                <button onClick={closeModal} className="w-full py-3 px-4 bg-[#0F766E] text-white font-semibold rounded-lg hover:bg-[#0D9488] transition-colors" data-id="element-148">
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}